
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import AdminHeader from '../../../components/feature/AdminHeader';

interface FieldNews {
  id: string;
  title: string;
  content: string;
  mission_field: string;
  category: string;
  news_date: string;
  display_order: number;
  images: { id: string; image_url: string; display_order: number }[];
}

interface PreviewFile {
  file: File;
  previewUrl: string;
}

export default function AdminFieldNewsPage() {
  const [newsList, setNewsList] = useState<FieldNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    mission_field: '',
    category: '',
    news_date: new Date().toISOString().split('T')[0],
    display_order: 0
  });

  const [previewFiles, setPreviewFiles] = useState<PreviewFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchNews();
  }, []);

  // 미리보기 URL 정리
  useEffect(() => {
    return () => {
      previewFiles.forEach(pf => URL.revokeObjectURL(pf.previewUrl));
    };
  }, [previewFiles]);

  const fetchNews = async () => {
    try {
      const { data: newsData, error: newsError } = await supabase
        .from('field_news')
        .select('*')
        .order('news_date', { ascending: false });

      if (newsError) throw newsError;

      const newsWithImages = await Promise.all(
        (newsData || []).map(async (news) => {
          const { data: images } = await supabase
            .from('field_news_images')
            .select('*')
            .eq('news_id', news.id)
            .order('display_order', { ascending: true });

          return { ...news, images: images || [] };
        })
      );

      setNewsList(newsWithImages);
    } catch (error) {
      console.error('Error fetching news:', error);
      alert('뉴스를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files);
    const remaining = 10 - previewFiles.length;

    if (remaining <= 0) {
      alert('이미지는 최대 10장까지 업로드할 수 있습니다.');
      return;
    }

    const filesToAdd = newFiles.slice(0, remaining);

    if (newFiles.length > remaining) {
      alert(`최대 10장까지 가능합니다. ${filesToAdd.length}장만 추가됩니다.`);
    }

    const newPreviews: PreviewFile[] = filesToAdd.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file)
    }));

    setPreviewFiles(prev => [...prev, ...newPreviews]);

    // input 초기화 (같은 파일 재선택 가능하도록)
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePreviewFile = (index: number) => {
    setPreviewFiles(prev => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadImages = async (newsId: string) => {
    const total = previewFiles.length;

    for (let i = 0; i < total; i++) {
      const { file } = previewFiles[i];
      const fileExt = file.name.split('.').pop();
      const fileName = `${newsId}_${Date.now()}_${i}.${fileExt}`;
      const filePath = `field-news/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        continue;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      await supabase.from('field_news_images').insert({
        news_id: newsId,
        image_url: publicUrl,
        display_order: i
      });

      setUploadProgress(Math.round(((i + 1) / total) * 100));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    setUploadProgress(0);

    try {
      if (editingId) {
        const { error } = await supabase
          .from('field_news')
          .update(formData)
          .eq('id', editingId);

        if (error) throw error;

        if (previewFiles.length > 0) {
          await uploadImages(editingId);
        }

        alert('뉴스가 수정되었습니다.');
        setEditingId(null);
      } else {
        const { data, error } = await supabase
          .from('field_news')
          .insert([formData])
          .select()
          .single();

        if (error) throw error;

        if (previewFiles.length > 0 && data) {
          await uploadImages(data.id);
        }

        alert('뉴스가 추가되었습니다.');
      }

      setFormData({
        title: '',
        content: '',
        mission_field: '',
        category: '',
        news_date: new Date().toISOString().split('T')[0],
        display_order: 0
      });
      setPreviewFiles([]);
      setUploadProgress(0);
      fetchNews();
    } catch (error) {
      console.error('Error:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (news: FieldNews) => {
    setEditingId(news.id);
    setFormData({
      title: news.title,
      content: news.content,
      mission_field: news.mission_field,
      category: news.category,
      news_date: news.news_date,
      display_order: news.display_order
    });
    setPreviewFiles([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('field_news')
        .delete()
        .eq('id', id);

      if (error) throw error;

      alert('삭제되었습니다.');
      fetchNews();
    } catch (error) {
      console.error('Error:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!confirm('이미지를 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('field_news_images')
        .delete()
        .eq('id', imageId);

      if (error) throw error;

      fetchNews();
    } catch (error) {
      console.error('Error:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="선교지 소식 관리" subtitle="선교지 소식을 관리합니다" />

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-12">
        {/* Form */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">
            {editingId ? '뉴스 수정' : '새 뉴스 추가'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">제목 *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="뉴스 제목을 입력하세요"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">내용</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={5}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="뉴스 내용을 입력하세요"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">선교지</label>
                <input
                  type="text"
                  value={formData.mission_field}
                  onChange={(e) => setFormData({ ...formData, mission_field: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="예: 캄보디아, 태국"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">카테고리</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="예: 교육, 의료, 건축"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">날짜 *</label>
                <input
                  type="date"
                  required
                  value={formData.news_date}
                  onChange={(e) => setFormData({ ...formData, news_date: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">표시 순서</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="0"
                />
              </div>
            </div>

            {/* 이미지 업로드 영역 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  이미지 업로드
                  <span className="ml-2 text-xs text-gray-400 font-normal">
                    ({previewFiles.length}/10장)
                  </span>
                </label>
                {previewFiles.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      previewFiles.forEach(pf => URL.revokeObjectURL(pf.previewUrl));
                      setPreviewFiles([]);
                    }}
                    className="text-xs text-red-500 hover:text-red-700 cursor-pointer"
                  >
                    전체 삭제
                  </button>
                )}
              </div>

              {/* 드래그 업로드 영역 */}
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                  previewFiles.length >= 10
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    : 'border-teal-300 hover:border-teal-500 hover:bg-teal-50'
                }`}
                onClick={() => previewFiles.length < 10 && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={previewFiles.length >= 10}
                />
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                  <i className={`ri-image-add-line text-3xl ${previewFiles.length >= 10 ? 'text-gray-300' : 'text-teal-400'}`}></i>
                </div>
                {previewFiles.length >= 10 ? (
                  <p className="text-sm text-gray-400">최대 10장이 선택되었습니다</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-teal-600">클릭하여 이미지 선택</p>
                    <p className="text-xs text-gray-400 mt-1">
                      최대 10장 · JPG, PNG, GIF 등 이미지 파일
                    </p>
                    <p className="text-xs text-gray-400">
                      현재 {previewFiles.length}장 선택됨 · {10 - previewFiles.length}장 더 추가 가능
                    </p>
                  </>
                )}
              </div>

              {/* 이미지 미리보기 그리드 */}
              {previewFiles.length > 0 && (
                <div className="mt-3 grid grid-cols-5 gap-2">
                  {previewFiles.map((pf, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img
                        src={pf.previewUrl}
                        alt={`미리보기 ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg border border-gray-200"
                      />
                      <div className="absolute inset-0 bg-black/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <button
                        type="button"
                        onClick={() => removePreviewFile(index)}
                        className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-red-600"
                        title="이미지 제거"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                      <div className="absolute bottom-1 left-1 bg-black/50 text-white text-[10px] px-1 rounded">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 업로드 진행률 */}
              {uploading && previewFiles.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>업로드 중...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={uploading}
                className="w-full sm:w-auto px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 whitespace-nowrap cursor-pointer"
              >
                {uploading
                  ? `업로드 중... (${uploadProgress}%)`
                  : editingId ? '수정하기' : '추가하기'}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setFormData({
                      title: '',
                      content: '',
                      mission_field: '',
                      category: '',
                      news_date: new Date().toISOString().split('T')[0],
                      display_order: 0
                    });
                    previewFiles.forEach(pf => URL.revokeObjectURL(pf.previewUrl));
                    setPreviewFiles([]);
                  }}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">등록된 뉴스 목록</h2>

          {newsList.length === 0 ? (
            <p className="text-gray-500 text-center py-8">등록된 뉴스가 없습니다.</p>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {newsList.map((news) => (
                <div key={news.id} className="border border-gray-200 rounded-lg p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">{news.title}</h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {news.mission_field && (
                          <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs sm:text-sm">
                            {news.mission_field}
                          </span>
                        )}
                        {news.category && (
                          <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs sm:text-sm">
                            {news.category}
                          </span>
                        )}
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs sm:text-sm">
                          {news.news_date}
                        </span>
                      </div>
                      {news.content && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{news.content}</p>
                      )}
                    </div>

                    <div className="flex gap-2 self-end sm:self-start">
                      <button
                        onClick={() => handleEdit(news)}
                        className="flex-1 sm:flex-none px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDelete(news.id)}
                        className="flex-1 sm:flex-none px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                      >
                        삭제
                      </button>
                    </div>
                  </div>

                  {news.images.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">첨부 이미지 {news.images.length}장</p>
                      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {news.images.map((image) => (
                          <div key={image.id} className="relative group aspect-square">
                            <img
                              src={image.image_url}
                              alt="뉴스 이미지"
                              className="w-full h-full object-cover rounded-lg"
                            />
                            <button
                              onClick={() => handleDeleteImage(image.id)}
                              className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                              title="이미지 삭제"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
